// <purpose>Pure meta helpers over the D3 reply engine's labels-only envelope
// (SuggestionInfo.replyEngine): the zone chip meta (GRÜN/GELB/ROT) and the isNoDraft predicate
// consumed by the render branch AND the action guards (Kopieren/Genehmigen/Download).</purpose>
// <dependencies>theme.ts zone tokens; types.ts SuggestionInfo.</dependencies>
// <constraints>Zones are exactly GRÜN/GELB/ROT — suppression is a reason, never a fourth
// zone. Fail-open: an unknown zone renders raw rather than throwing, because the envelope's
// vocabulary may grow ahead of this map. Why a row has no letter is the Begründung's to say (part
// 5-2, 2026-10-02: the served proposal, warning and procedure lines) — the code-owned no-draft
// explanation, the escalation-reason labels and the override-id nouns this file used to carry are
// deleted with their last readers (the retired no-draft card, the triage panel's override badge).</constraints>
import { theme } from '../theme';
import { SuggestionInfo } from '../serverTypes';

export interface ZoneMeta {
  label: string;
  fg: string;
  bg: string;
  border: string;
  legend: string;
}

const ZONES: Record<string, ZoneMeta> = {
  Gruen: { ...theme.colors.zone.gruen, label: 'GRÜN', legend: 'Kein Handlungsbedarf — Versand/Ablage freigegeben' },
  Gelb: { ...theme.colors.zone.gelb, label: 'GELB', legend: 'Bitte prüfen' },
  Rot: { ...theme.colors.zone.rot, label: 'ROT', legend: 'Menschliche Bearbeitung erforderlich' },
};

// The widest doctrine label — ZoneChip's invisible placeholder uses this text (never a guess)
// so the pre-allocated box is at least as wide as any real zone chip that might replace it.
export function widestZoneLabel(): string {
  return Object.values(ZONES).reduce((w, z) => (z.label.length > w.length ? z.label : w), '');
}

export function zoneMeta(zone: string | undefined | null): ZoneMeta {
  if (zone && ZONES[zone]) return ZONES[zone];
  // Fail-open: an unknown zone renders raw and neutral instead of hiding or crashing.
  return {
    label: zone ?? '—',
    fg: theme.colors.navyMid,
    bg: theme.colors.bgLight,
    border: theme.colors.borderLight,
    legend: '',
  };
}

// A deliberate engine no-draft: envelope present AND the displayed text (edit wins) is blank.
export function isNoDraft(suggestion: SuggestionInfo | null | undefined): boolean {
  if (!suggestion?.replyEngine) return false;
  const displayed = suggestion.editedText ?? suggestion.suggestionText;
  return !displayed || displayed.trim().length === 0;
}
